/**
 * Welcome Kit — free gift for subscriptions
 *
 * ONE kit per order. Any number of subscription lines earns exactly one kit;
 * no subscription means no kit. (Until 7 Oct 2026 the rule was one kit per
 * subscription LINE — see plan() for the one-line change that restores it.)
 *
 * Correctness comes from the Shopify cart API, never from the DOM.
 *
 * The kit line is hidden in the cart drawer (see welcome-kit-data.liquid) and
 * appears only at checkout, so none of this is ever on screen.
 *
 * 'cart:updated' on document drives the kit: the native mini cart fires it
 * after every redraw (and Monster Cart, when it was on, with the new item list
 * on every change). Adds, removals and quantity changes all arrive here.
 *
 * Checkout: no check of its own. The single Checkout check in
 * free-gift-cart.js fixes the kit and the tier gifts together, using plan()
 * from here (see "checkout" below).
 *
 * Loaded above {{ content_for_header }} so it is in place before app scripts.
 * Settings come from window.BescherWelcomeKit (snippets/welcome-kit-data.liquid).
 *
 * Debug: localStorage.setItem('welcome_kit_debug', '1')
 */
(function () {
  'use strict';

  /* Not _free_gift: the cart app uses that name for its own gifts, and claims
     any product whose Shopify product type is "free_gift". Ours avoids both. */
  var MARKER_KEY = '_welcome_kit';
  var MARKER_VALUE = 'bescher';

  /* Let a burst of changes settle before writing. One removal makes the app
     send a change and then an update, and that update resends its own view of
     every line — which re-creates a line removed in between. */
  var QUIET_MS = 250;
  /* One retry covers losing a race for the cart lock (HTTP 422). */
  var RETRY_MS = 1200;
  /* Stop rather than join a runaway involving another app. */
  var MAX_WRITES = 6;
  var WRITE_WINDOW_MS = 10000;
  /* How long an item list handed to us by the app stays trustworthy. */
  var SNAPSHOT_TTL_MS = 2000;

  /* Captured before any app can wrap it. */
  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;

  var startedAt = Date.now();
  var timer = null;
  var running = false;
  var queued = false;
  var stopped = false;
  var retried = false;
  var writeLog = [];
  var snapshot = null;
  var snapshotAt = 0;
  /* The change in progress, so the Checkout check can wait for it to land. */
  var active = null;
  /* Set by the Checkout check while it fixes the cart: nothing new starts. */
  var paused = false;

  function settings() {
    return window.BescherWelcomeKit || {};
  }

  function debug() {
    var on = settings().debug;

    if (!on) {
      try {
        on = window.localStorage.getItem('welcome_kit_debug') === '1';
      } catch (e) {
        return;
      }
    }

    if (!on) return;

    try {
      var at = '+' + ((Date.now() - startedAt) / 1000).toFixed(2) + 's';
      console.log.apply(console, ['[welcome-kit ' + at + ']'].concat(Array.prototype.slice.call(arguments)));
    } catch (e) {
      /* logging must never break the cart */
    }
  }

  /* An array even though one product is used today: the logic loops. */
  function kitProducts() {
    var config = settings();

    if (!config.enabled || !Array.isArray(config.cartItems)) return [];

    return config.cartItems.filter(function (item) {
      return item && item.variantId;
    });
  }

  function overBudget() {
    var now = Date.now();

    writeLog = writeLog.filter(function (at) {
      return now - at < WRITE_WINDOW_MS;
    });

    if (writeLog.length < MAX_WRITES) return false;

    stopped = true;

    try {
      console.warn('[welcome-kit] too many cart writes in a row — stopping. Reload to resume.');
    } catch (e) {
      /* no-op */
    }

    return true;
  }

  /* --- the cart ----------------------------------------------------------- */

  function getCart() {
    var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';

    return nativeFetch(root + 'cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then(function (response) {
      return response.json();
    });
  }

  /* Shopify serializes cart access, so a read issued while the app is writing
     queues behind it — measured at two seconds on this store. The app hands us
     its item list with every 'cart:updated', so most of the time we already
     have what a read would have told us. */
  function readCart() {
    if (snapshot && Date.now() - snapshotAt < SNAPSHOT_TTL_MS) {
      var fresh = snapshot;
      snapshot = null;
      debug('using the item list the app just published, no read needed');
      return Promise.resolve(fresh);
    }

    return getCart();
  }

  function post(path, body) {
    debug('POST', path, body);

    return nativeFetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }).then(function (response) {
      if (response.ok) return response.json();

      return response.text().then(function (text) {
        debug('POST ' + path + ' rejected — HTTP ' + response.status, text);
        var error = new Error('Welcome Kit: ' + path + ' failed (' + response.status + ')');
        error.status = response.status;
        throw error;
      });
    });
  }

  /* Sequential: the cart API does not take concurrent writes. */
  function apply(operations) {
    return operations.reduce(function (chain, operation) {
      return chain.then(function () {
        if (operation.add) return post('/cart/add.js', { items: [operation.add] });
        return post('/cart/change.js', { id: operation.id, quantity: operation.quantity });
      });
    }, Promise.resolve());
  }

  /* monster_refresh(open, done) re-reads the cart and pushes it into the app's
     own state. The first argument decides whether it also OPENS the drawer and
     defaults to true, which is why a redraw arriving after the customer closed
     the drawer used to pop it back up. False refreshes silently.

     Not optional: the app holds the cart in memory and does not re-read it when
     the drawer opens, so skipping this leaves the drawer showing a stale cart —
     and a stale cart is what re-creates a line we just removed. */
  function refreshDrawer() {
    try {
      if (typeof window.monster_refresh === 'function') window.monster_refresh(false);
      /* Native mini cart: redraws without opening the drawer. */
      else if (window.PROJ && window.PROJ.ajaxcart) window.PROJ.ajaxcart.updateCart();
    } catch (e) {
      /* never let a drawer redraw break cart state */
    }
  }

  /* --- what the cart should look like -------------------------------------- */

  function isKitLine(item) {
    return !!(item && item.properties && item.properties[MARKER_KEY] === MARKER_VALUE);
  }

  /* Counts LINES, not units. */
  function countSubscriptionLines(cart) {
    var items = (cart && cart.items) || [];
    var count = 0;

    for (var i = 0; i < items.length; i++) {
      if (items[i].selling_plan_allocation) count++;
    }

    return count;
  }

  function buildProperties() {
    var properties = {};
    properties[MARKER_KEY] = MARKER_VALUE;
    return properties;
  }

  /* Returns the operations needed, empty when the cart is already correct. */
  function plan(cart) {
    var items = (cart && cart.items) || [];
    var products = kitProducts();
    /* One kit per ORDER: the count only decides whether a kit is owed at all.
       Previous rule, one kit per subscription line, was:
         var expected = countSubscriptionLines(cart); */
    var expected = countSubscriptionLines(cart) > 0 ? 1 : 0;
    var operations = [];
    var seen = {};
    var i;

    for (i = 0; i < items.length; i++) {
      if (!isKitLine(items[i])) continue;

      var variantId = items[i].variant_id;
      var configured = products.some(function (product) {
        return String(product.variantId) === String(variantId);
      });

      /* Kits live on ONE line with quantity N. A duplicate line, or one for a
         product no longer configured, is cleared. */
      if (!configured || seen[variantId]) {
        operations.push({ id: items[i].key, quantity: 0 });
        continue;
      }

      seen[variantId] = items[i];
    }

    for (i = 0; i < products.length; i++) {
      var product = products[i];
      var wanted = expected * (product.quantity || 1);
      var line = seen[product.variantId];

      if (!line && wanted > 0) {
        operations.push({ add: { id: product.variantId, quantity: wanted, properties: buildProperties() } });
      } else if (line && line.quantity !== wanted) {
        /* By key, never by id: two lines can share a variant id. */
        operations.push({ id: line.key, quantity: wanted });
      }
    }

    return operations;
  }

  function reconcile() {
    if (!nativeFetch || stopped || paused) return Promise.resolve();

    /* Disabled still runs: kitProducts() is empty then, so plan() adds nothing
       and removes any kit line already in the cart. */

    if (running) {
      queued = true;
      return Promise.resolve();
    }

    running = true;

    active = readCart()
      .then(function (cart) {
        var operations = plan(cart);

        if (!operations.length) {
          debug('cart already correct, nothing to do');
          retried = false;
          return null;
        }

        if (overBudget()) return null;

        writeLog.push(Date.now());
        debug('applying', operations);
        snapshot = null;

        return apply(operations).then(function () {
          debug('syncing the cart app, without opening the drawer');
          refreshDrawer();
        });
      })
      .catch(function (error) {
        debug('reconcile failed', error && error.message);

        if (error && error.status === 422 && !retried) {
          retried = true;
          debug('cart was busy — retrying once in ' + RETRY_MS + 'ms');
          setTimeout(function () {
            running = false;
            reconcile();
          }, RETRY_MS);
        }
      })
      .then(function () {
        running = false;
        active = null;

        if (queued) {
          queued = false;
          schedule();
        }
      });

    return active;
  }

  function schedule() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(reconcile, QUIET_MS);
  }

  /* --- adding --------------------------------------------------------------
     Nothing here. An add reaches us the same way a removal does, through
     'cart:updated', and the kit follows a beat later.

     It used to go in front of the app's add, using its mu_custom_atc_logic
     hook, so the kit landed first. That bought nothing: the kit row is hidden,
     so no customer ever saw the difference, and it put our write on the
     critical path between a customer and buying something. The Checkout check
     (free-gift-cart.js) is what makes the cart right, so nothing else has to
     be quick.

     Detecting subscriptions from the cart rather than from the PDP form also
     covers adds the form never saw — drawer upsells, quick-add, anywhere. */

  /* --- checkout ------------------------------------------------------------
     No check of its own. One Checkout check, in free-gift-cart.js, fixes the
     kit and the tier gifts together, in the drawer and on the /cart page, and
     always runs — kit switched off included (plan() then removes any kit).

     Why not here: the kit used to hold the Checkout click, check, then click
     the button again. When the drawer redrew during that check, the button it
     clicked was no longer on the page, so nothing happened and the customer
     had to click twice.

     What the check needs from here:
       idle()       resolves once a change already on its way has landed, so
                    its fresh read cannot miss it
       pause(on)    stops new changes while it works; off again, the kit
                    catches up with whatever it missed
       plan(cart)   the kit's fixes for a cart, unchanged from above */
  window.BescherWelcomeKitCheckout = {
    idle: function () {
      return active || Promise.resolve();
    },
    pause: function (on) {
      paused = !!on;

      if (paused) {
        if (timer) clearTimeout(timer);
      } else {
        schedule();
      }
    },
    plan: plan,
  };

  /* --- following the cart --------------------------------------------------
     The app dispatches 'cart:updated' on document whenever its item list
     changes, and attaches the list. That covers removals, quantity changes and
     plan switches, and it fires on the optimistic update too — the instant the
     customer clicks the bin, not when the server answers.

     Its items are real Shopify line objects, so they can be used as they are. A
     list without keys is not one, and is ignored in favour of a read.

     The native mini cart fires the same event, without items, after every
     redraw (PROJ.ajaxcart.updateCart in theme-bescher.js), so it always reads. */

  document.addEventListener('cart:updated', function (event) {
    try {
      var items = event && event.detail ? event.detail.items : null;

      var usable =
        Array.isArray(items) &&
        items.every(function (item) {
          return item && item.key;
        });

      if (usable) {
        snapshot = { items: items };
        snapshotAt = Date.now();
      }

      debug('the app published a new cart');
      schedule();
    } catch (e) {
      schedule();
    }
  });

  /* --- page load -----------------------------------------------------------
     Safety net for a customer who left before an add finished, and for a cart
     changed elsewhere. One read per page load. */

  function reconcileOnLoad() {
    debug('page load check');
    reconcile();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcileOnLoad);
  } else {
    reconcileOnLoad();
  }

  /* For manual checks in the console. */
  window.BescherWelcomeKitReconcile = reconcile;
})();
